import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Link2, Loader2, RefreshCw } from 'lucide-react'
import { exchangeEmbeddedSignup, reauthEmbeddedSignup } from '../api'
import { Button } from './ui/button'

// Meta no tipa esto — se declara lo mínimo que se usa acá, no todo el SDK.
declare global {
  interface Window {
    fbAsyncInit?: () => void
    FB?: {
      init: (params: { appId: string; version: string; xfbml?: boolean }) => void
      login: (
        callback: (response: { authResponse?: { code?: string } }) => void,
        options: {
          config_id: string
          response_type: 'code'
          override_default_response_type: true
          extras: { version: number; sessionInfoVersion: string }
        },
      ) => void
    }
  }
}

const FB_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js'
const FB_SDK_VERSION = 'v21.0'
// Meta postea la session info del flujo embebido desde uno de estos dos
// orígenes según el punto del flujo — se aceptan ambos, cualquier otro se
// ignora (no es tráfico del SDK de Meta).
const TRUSTED_MESSAGE_ORIGINS = new Set(['https://www.facebook.com', 'https://web.facebook.com'])

let fbSdkPromise: Promise<void> | null = null

function loadFacebookSdk(appId: string): Promise<void> {
  if (fbSdkPromise) return fbSdkPromise
  fbSdkPromise = new Promise((resolve) => {
    window.fbAsyncInit = () => {
      window.FB!.init({ appId, version: FB_SDK_VERSION, xfbml: false })
      resolve()
    }
    const script = document.createElement('script')
    script.src = FB_SDK_URL
    script.async = true
    script.defer = true
    document.body.appendChild(script)
  })
  return fbSdkPromise
}

interface SessionInfo {
  waba_id?: string
  phone_number_id?: string
}

interface Props {
  sociedadId: string
  /** Si se pasa, re-conecta ESTE canal (reauth) en vez de crear uno nuevo. */
  channelAccountId?: string
  label?: string
  onConnected?: () => void
}

export function EmbeddedSignupButton({ sociedadId, channelAccountId, label, onConnected }: Props) {
  const appId = import.meta.env.VITE_META_APP_ID as string | undefined
  const configId = import.meta.env.VITE_META_CONFIG_ID as string | undefined
  const [sdkReady, setSdkReady] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const sessionInfoRef = useRef<SessionInfo | null>(null)
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!appId) return
    let cancelled = false
    loadFacebookSdk(appId).then(() => {
      if (!cancelled) setSdkReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [appId])

  useEffect(() => {
    function handleMessage(event: MessageEvent): void {
      if (!TRUSTED_MESSAGE_ORIGINS.has(event.origin)) return
      let data: { type?: string; event?: string; data?: SessionInfo }
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
      } catch {
        return
      }
      if (data?.type !== 'WA_EMBEDDED_SIGNUP') return
      if (data.event === 'FINISH' || data.event === 'FINISH_ONLY_WABA') {
        sessionInfoRef.current = { waba_id: data.data?.waba_id, phone_number_id: data.data?.phone_number_id }
        setStatus('Verificando el número con Meta…')
      } else if (data.event === 'CANCEL') {
        setStatus(null)
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  const mutation = useMutation({
    mutationFn: (payload: { code: string; waba_id: string; phone_number_id: string }) =>
      channelAccountId ? reauthEmbeddedSignup(channelAccountId, payload) : exchangeEmbeddedSignup(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-accounts', sociedadId] })
      toast.success(channelAccountId ? 'Conexión renovada.' : 'Número de WhatsApp conectado.')
      setStatus(null)
      onConnected?.()
    },
    onError: () => {
      toast.error('No se pudo completar la conexión con Meta.')
      setStatus(null)
    },
  })

  const handleClick = useCallback(() => {
    if (!configId || !window.FB) return
    sessionInfoRef.current = null
    setStatus('Abriendo el flujo de Meta…')
    window.FB.login(
      (response) => {
        const code = response.authResponse?.code
        if (!code) {
          setStatus(null)
          return
        }
        const session = sessionInfoRef.current
        if (!session?.waba_id || !session?.phone_number_id) {
          toast.error('Meta no devolvió el número conectado — intentá de nuevo.')
          setStatus(null)
          return
        }
        setStatus('Conectando el número…')
        mutation.mutate({ code, waba_id: session.waba_id, phone_number_id: session.phone_number_id })
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: { version: 4, sessionInfoVersion: '3' },
      },
    )
  }, [configId, mutation])

  if (!appId || !configId) {
    // Todavía no se cargó VITE_META_APP_ID/VITE_META_CONFIG_ID (o la
    // plataforma todavía no está aprobada como Tech Provider ante Meta) — la
    // configuración manual sigue siendo el camino real mientras tanto.
    return null
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Button
        type="button"
        variant={channelAccountId ? 'outline' : 'default'}
        size={channelAccountId ? 'sm' : 'default'}
        disabled={!sdkReady || mutation.isPending}
        onClick={handleClick}
      >
        {mutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : channelAccountId ? (
          <RefreshCw className="h-3.5 w-3.5" />
        ) : (
          <Link2 className="h-4 w-4" />
        )}
        {label ?? (channelAccountId ? 'Reconectar' : 'Conectar WhatsApp con Meta')}
      </Button>
      {status && <p className="text-xs text-muted-foreground">{status}</p>}
    </div>
  )
}
